/**
 * Lessons API + TanStack Query hooks.
 * Endpoint catalog: docs/API-CONTRACT.md → "Lessons".
 *
 * Read serializers/views in `backend/lessons/` for the shape contract.
 * Detail responses include description, attendees (PK list), google_meet_link,
 * classpoint_link and a tutor_name convenience. The list serializer adds
 * attendee_count.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api } from "./client";
import type { LessonGoogle } from "./google";
import { toQueryString, type ListQuery, type Paginated } from "./types";

/* ─────────────────────────────────────────── Shapes */

export interface LessonListItem {
  id: number | string;
  title?: string;
  /** Subject code as exposed by the list serializer (SlugRelatedField). */
  subject?: string;
  scheduled_at: string;
  duration_minutes?: number;
  status?: string;
  tutor?: { id: number | string; full_name?: string } | null;
  tutor_id?: number | string;
  tutor_name?: string;
  attendee_count?: number;
  /** ISO instant the lesson finishes (start plus duration). */
  ends_at?: string | null;
  /** True once the tutor has written the wrap-up. */
  has_report?: boolean;
  /** Optional convenience the dashboard endpoint exposes. */
  student?: { id: number | string; full_name?: string } | null;
  student_name?: string;
  student_id?: number | string;
}

/** Attendance codes, seeded in lookups (model "attendance"). */
export type AttendanceCode = "present" | "late" | "absent";

export interface LessonAttendanceRow {
  student: number;
  student_name: string;
  status: AttendanceCode;
  /** 1 (shaky) to 5 (secure), or null when not recorded. */
  confidence: number | null;
  comment: string;
}

export interface LessonReport {
  summary: string;
  next_steps: string;
  topics: string[];
  /** Only the rows the viewer may see: all for staff, their own child for families. */
  attendance: LessonAttendanceRow[];
  written_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface LessonReportInput {
  summary: string;
  next_steps?: string;
  topics?: string[];
  attendance: Array<{ student: number; status: AttendanceCode; confidence?: number | null; comment?: string }>;
}

export interface LessonAttendee {
  id: number;
  full_name: string;
  avatar: string | null;
  year_group: string | null;
}

export interface LessonDetail extends LessonListItem {
  report?: LessonReport | null;
  /** Attendees the viewer may see, with names. Families only get their own child. */
  attendee_details?: LessonAttendee[];
  description?: string;
  attendees?: Array<number | string>;
  google_meet_link?: string;
  classpoint_link?: string;
  /** Calendar sync state; `recording` is present for admin only. */
  google?: LessonGoogle;
  created_by?: number | string | null;
  created_by_email?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface LessonResource {
  id: number | string;
  title: string;
  subject?: string;
  lesson?: number | string;
  file_count?: number;
  created_at?: string;
  files?: Array<{
    id: number | string;
    name: string;
    /** Auth-checked API path (open with the file viewer). */
    file: string | null;
    content_type?: string;
  }>;
}

export interface LessonListQuery extends ListQuery {
  tutor?: number | string;
  student?: number | string;
  subject?: number | string;
  status?: number | string;
  /** ISO datetime, maps to backend `date_from`. */
  date_from?: string;
  /** ISO datetime, maps to backend `date_to`. */
  date_to?: string;
  /** true: finished, not cancelled, no wrap-up yet. */
  needs_report?: boolean;
}

export interface LessonWriteInput {
  title: string;
  description?: string;
  tutor: number | string;
  attendees?: Array<number | string>;
  subject?: number | string | null;
  scheduled_at: string;
  duration_minutes?: number;
  google_meet_link?: string;
  classpoint_link?: string;
  status?: number | string | null;
}

/* ─────────────────────────────────────────── Endpoints */

export function listLessons(
  q: LessonListQuery = {},
): Promise<Paginated<LessonListItem>> {
  return api
    .get<Paginated<LessonListItem>>(`/lessons/${toQueryString(q)}`)
    .then((r) => r.data);
}

export function getLesson(id: number | string): Promise<LessonDetail> {
  return api.get<LessonDetail>(`/lessons/${id}/`).then((r) => r.data);
}

export function createLesson(input: LessonWriteInput): Promise<LessonDetail> {
  return api.post<LessonDetail>("/lessons/", input).then((r) => r.data);
}

export function updateLesson(
  id: number | string,
  input: Partial<LessonWriteInput>,
): Promise<LessonDetail> {
  return api.patch<LessonDetail>(`/lessons/${id}/`, input).then((r) => r.data);
}

export function deleteLesson(id: number | string): Promise<void> {
  return api.delete<void>(`/lessons/${id}/`).then(() => undefined);
}

export function markLessonComplete(
  id: number | string,
): Promise<LessonDetail> {
  return api
    .post<LessonDetail>(`/lessons/${id}/mark-complete/`)
    .then((r) => r.data);
}

export function saveLessonReport(id: number | string, input: LessonReportInput): Promise<LessonDetail> {
  return api.put<LessonDetail>(`/lessons/${id}/report/`, input).then((r) => r.data);
}

export function listLessonResources(
  id: number | string,
): Promise<Paginated<LessonResource> | LessonResource[]> {
  return api
    .get<Paginated<LessonResource> | LessonResource[]>(`/lessons/${id}/resources/`)
    .then((r) => r.data);
}

/* ─────────────────────────────────────────── Queries */

export function useLessons(
  q: LessonListQuery = {},
  enabled = true,
): UseQueryResult<Paginated<LessonListItem>> {
  return useQuery({
    queryKey: ["lessons", q],
    queryFn: () => listLessons(q),
    enabled,
    // A filter change over the same period keeps the current lessons on
    // screen until the new list lands, so filtering never re-skeletons. A
    // new period starts clean: last week's lessons are not this week's.
    placeholderData: (prev, prevQuery) => {
      const pq = prevQuery?.queryKey[1] as LessonListQuery | undefined;
      return pq && pq.date_from === q.date_from && pq.date_to === q.date_to ? prev : undefined;
    },
  });
}

export function useLessonDetail(
  id: number | string | null | undefined,
  enabled = true,
): UseQueryResult<LessonDetail> {
  return useQuery({
    queryKey: ["lesson", id],
    queryFn: () => getLesson(id as number | string),
    enabled: enabled && id != null && id !== "",
  });
}

export function useLessonResources(
  id: number | string | null | undefined,
  enabled = true,
): UseQueryResult<LessonResource[]> {
  return useQuery({
    queryKey: ["lesson", id, "resources"],
    queryFn: async () => {
      const r = await listLessonResources(id as number | string);
      if (Array.isArray(r)) return r;
      return r.results ?? [];
    },
    enabled: enabled && id != null && id !== "",
  });
}

/* ─────────────────────────────────────────── Mutations */

function invalidateLessons(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ["lessons"] });
  void qc.invalidateQueries({ queryKey: ["dashboard"] });
}

export function useCreateLesson(): UseMutationResult<
  LessonDetail,
  unknown,
  LessonWriteInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LessonWriteInput) => createLesson(input),
    onSuccess: () => invalidateLessons(qc),
  });
}

export function useUpdateLesson(): UseMutationResult<
  LessonDetail,
  unknown,
  { id: number | string; input: Partial<LessonWriteInput> }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }) => updateLesson(id, input),
    onSuccess: (lesson) => {
      invalidateLessons(qc);
      void qc.invalidateQueries({ queryKey: ["lesson", lesson.id] });
    },
  });
}

export function useDeleteLesson(): UseMutationResult<
  void,
  unknown,
  number | string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => deleteLesson(id),
    onSuccess: () => invalidateLessons(qc),
  });
}

export function useMarkLessonComplete(): UseMutationResult<
  LessonDetail,
  unknown,
  number | string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => markLessonComplete(id),
    onSuccess: (lesson) => {
      invalidateLessons(qc);
      void qc.invalidateQueries({ queryKey: ["lesson", lesson.id] });
    },
  });
}

/** Finished lessons still waiting for a wrap-up, oldest first. */
export function useWrapUpsDue(enabled = true): UseQueryResult<Paginated<LessonListItem>> {
  return useLessons({ needs_report: true, ordering: "scheduled_at", page_size: 50 }, enabled);
}

export function useSaveLessonReport(): UseMutationResult<
  LessonDetail,
  unknown,
  { id: number | string; input: LessonReportInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }) => saveLessonReport(id, input),
    onSuccess: (lesson) => {
      qc.setQueryData(["lesson", lesson.id], lesson);
      qc.setQueryData(["lesson", String(lesson.id)], lesson);
      invalidateLessons(qc);
    },
  });
}
